<script setup>
const tech = useTech()

const config = useRuntimeConfig()
const { formatTitle } = useHelpers()
const { params, fullPath } = useRoute()
const certificates = useCertificates()
const certificate = await certificates.getOne({ id: params.id })
const techTheme = useTechTheme(certificate?.exam.certification_id)
const theme = techTheme.currentTheme

const page = usePages().getOne({ slug: 'sales-page' })
const url = config.public.APP_ROOT + fullPath

const title = computed(() => formatTitle(certificate.certification.name))
const isDarkVersion = ref(certificate.dark)
const description = ref(`is a certified ${tech.techLabel} developer. They have demonstrated the skills and knowledge necessary to develop single-page web applications using ${tech.techLabel}.`)

const metaTitle = computed(() => certificate.candidate?.user?.name + ' - ' + certificate.certification?.name)
const metaDescription = computed(() => certificate.certification?.description)

const certificateImage = computed(() => `${config.public.API_URL}/certificates/thumbnail/${certificate.id}${isDarkVersion.value && tech.id !== 'angular' ? '.dark' : ''}.jpg`)
const certificateImageAlt = computed(() => `Certificate of ${certificate.display_name} for ${certificate.certification?.name}`)

const author = computed(() => ({
  vuejs: {
    image: '/images/certificate-evan.png',
    description: 'The examination was developed under the review of <span class="font-medium text-eerie-black dark:text-white">Evan You</span>, the creator of Vue.js together with interested members of the Vue.js Core team. The exam contained both multiple choice questions and practical coding exercises designed to validate the knowledge and skills required to build complex applications using Vue.js.'
  },
  nuxt: {
    image: '/images/board-sebastien.jpg',
    description: 'The examination was developed under the review of <span class="font-medium text-eerie-black dark:text-white">Sebastien Chopin</span>, the creator of Nuxt. The exam contained both multiple choice questions and practical coding exercises designed to validate the knowledge and skills required to build complex applications using Nuxt.'
  },
  angular: {
    image: '/images/board-alain.jpg',
    description: 'The Angular certification program was developed under the guidance of <span class="font-medium text-eerie-black dark:text-white">Alain Chautard</span>, a Google Developer Expert in Angular and Google Maps, in collaboration with a team of global experts. The certification process is designed to validate the knowledge and skills required to build complex applications using Angular.'
  }
}[tech.id]))

useSeoMeta({
  title: metaTitle.value,
  description: metaDescription.value,
  ogTitle: metaTitle.value,
  ogUrl: url,
  twitterUrl: url,
  twitterTitle: metaTitle.value,
  ogDescription: metaDescription.value,
  ogImage: certificateImage.value,
  twitterDescription: metaDescription.value,
  twitterImage: certificateImage.value,
  twitterCard: 'summary_large_image'
})

useHead(() => ({
  link: [
    {
      rel: 'canonical',
      href: [config.public.APP_ROOT, fullPath].join('')
    }
  ]
}))

</script>

<template>
  <LandingLayout>
    <div class="relative z-10 mx-auto max-w-screen-2xl p-4 md:p-10">
      <div class="flex flex-col gap-8 xl:flex-row xl:gap-16">
        <div
          class="flex h-fit shrink-0 flex-col gap-9 rounded-2xl bg-white p-8 xl:w-88 dark:bg-dark-gunmetal"
          :class="{' dark:bg-dark-metal' : tech.id === 'nuxt' }">
          <div
            class="flex flex-col gap-7.5">
            <div class="min-h-15">
              <InlineSvg
                :src="`/images/tech/${tech.id}/developer.svg`"
                class="size-15"
                :class="theme?.textColor || 'text-mint'"
                alt="Certification Logo" />
            </div>
            <div
              class="text-2xl font-bold"
              :class="theme?.heading || 'heading-primary'"
              v-html="title" />
          </div>
          <div class="grid gap-7.5">
            <div
              class="inline-block w-fit rounded-md px-3 py-2 text-xs font-normal"
              :class="[
                theme?.bgColorTransparent || 'bg-mint/20',
                theme?.textColor || 'text-mint'
              ]">
              <span class="text-black dark:text-white">Certified on: </span>
              {{ certificate.certified_at_formatted }}
            </div>

            <!-- name -->
            <p
              class="text-2xl font-bold"
              :class="theme?.heading || 'headiing-primary'">
              {{ certificate['display_name'] }}
            </p>

            <!-- Description -->
            <div
              class="space-y-4 font-light text-body-gray"
              v-html="description" />
          </div>
        </div>
        <div class="grid gap-8">
          <ImageLoader
            class="min-h-168 w-full overflow-hidden rounded-2xl"
            :alt="certificateImageAlt"
            :src="certificateImage" />
          <div
            v-if="author"
            class="flex w-full flex-col gap-6 rounded-2xl bg-white p-6 md:flex-row md:items-center md:gap-9 md:p-9 dark:bg-dark-gunmetal">
            <img
              :src="author?.image"
              class="size-[100px] shrink-0 rounded-[10px]"
              :alt="`author: certificate ${tech.id}`">
            <p
              class="text-quartz dark:text-body-gray"
              v-html="author?.description" />
          </div>
          <div class="mt-10 grid w-full gap-6 md:mt-20 md:grid-cols-2 md:gap-6">
            <div class="flex w-full flex-col gap-6 rounded-2xl bg-white p-6 md:gap-9 md:p-9 dark:bg-dark-gunmetal">
              <h5 class="heading-primary text-2xl font-bold">
                Get <em>{{ tech.techLabel }} Certified</em>
              </h5>
              <p class="flex-1 text-base text-body-gray">
                Discover the benefits of getting an {{ tech.isOfficial ? 'Official' : '' }} {{ tech.techLabel }} Developer Certification for you and your team.
              </p>
              <NuxtLink
                :to="`/${tech.id}`"
                class="btn-primary w-full p-4">
                More Info
              </NuxtLink>
            </div>
            <div class="flex w-full flex-col gap-6 rounded-2xl bg-white p-6 md:gap-9 md:p-9 dark:bg-dark-gunmetal">
              <h5 class="heading-primary text-2xl font-bold">
                Hire <em>{{ tech.techLabel }} Certified</em> Developers
              </h5>
              <p class="flex-1 text-base text-body-gray">
                Equip your team with best developers.
              </p>
              <a
                href="https://vueschool.typeform.com/to/AugTNesx?typeform-source=certificates.dev"
                target="_blank"
                class="btn-primary w-full p-4">
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <AppFooter v-bind="{ page }" />
  </LandingLayout>
  <div class="pointer-events-none hidden select-none lg:block">
    <img
      :src="`/images/tech/${tech.id}-gradient-left.svg`"
      class="fixed left-0 top-0 -z-10 max-h-screen w-auto opacity-50 blur-2xl"
      alt="gradient-left">
    <img
      :src="`/images/tech/${tech.id}-gradient-right.svg`"
      class="fixed right-0 top-0 -z-10 max-h-screen w-auto opacity-50 blur-3xl"
      alt="gradient-right">
  </div>
</template>
